import { PermissionCategoryConfig, GROUP_ICONS, isOwnerOnlyActionKey } from '@/@types/TeamMember';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Shield } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PermissionsAccordionProps {
  permissions: Record<string, string[]>;
  onChange: (permissions: Record<string, string[]>) => void;
  categories: Record<string, PermissionCategoryConfig>;
}

const MotionDiv = motion.div;

export function PermissionsAccordion({
  permissions,
  onChange,
  categories,
}: PermissionsAccordionProps) {
  const { t } = useTranslation('team');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const assignablePermissions = (category: string) =>
    (categories[category]?.permissions ?? []).filter((p) => !isOwnerOnlyActionKey(p));

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const isCategoryFullyChecked = (category: string) => {
    const perms = assignablePermissions(category);
    const selected = permissions[category] || [];
    return perms.length > 0 && perms.every((p) => selected.includes(p));
  };

  const isCategoryPartiallyChecked = (category: string) => {
    const perms = assignablePermissions(category);
    const selected = permissions[category] || [];
    return selected.length > 0 && !perms.every((p) => selected.includes(p));
  };

  const toggleAllInCategory = (category: string) => {
    const perms = assignablePermissions(category);
    const isFullyChecked = isCategoryFullyChecked(category);
    const updated = { ...permissions };
    updated[category] = isFullyChecked ? [] : [...perms];
    onChange(updated);
  };

  const togglePermission = (category: string, permission: string) => {
    if (isOwnerOnlyActionKey(permission)) {
      return;
    }
    const current = permissions[category] || [];
    const updated = { ...permissions };
    if (current.includes(permission)) {
      updated[category] = current.filter((p) => p !== permission);
    } else {
      updated[category] = [...current, permission];
    }
    onChange(updated);
  };

  const isPermissionChecked = (category: string, permission: string) => {
    return (permissions[category] || []).includes(permission);
  };

  const getCheckedCount = (category: string) => {
    const perms = assignablePermissions(category);
    return (permissions[category] || []).filter((p) => perms.includes(p)).length;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.04 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 24 },
    },
  };

  return (
    <MotionDiv
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-2"
    >
      {Object.entries(categories).map(([category, config]) => {
        const visiblePermissions = assignablePermissions(category);
        if (visiblePermissions.length === 0) {
          return null;
        }

        const IconComponent = config.icon || GROUP_ICONS[category] || Shield;
        const isExpanded = expandedCategories[category];
        const checkedCount = getCheckedCount(category);
        const totalCount = visiblePermissions.length;

        return (
          <MotionDiv key={category} variants={itemVariants}>
            <div className="border border-colorBorder rounded-lg bg-card overflow-hidden">
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full p-3 flex items-center justify-between hover:bg-elevated/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-elevated flex items-center justify-center">
                    <IconComponent className="w-4 h-4 text-muted" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-sm">
                      {t(`categories.${category}`, { defaultValue: category })}
                    </h4>
                    <p className="text-xs text-muted">
                      {checkedCount}/{totalCount} {t('selected')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isCategoryFullyChecked(category) && (
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                  )}
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-muted transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <MotionDiv
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="border-t border-colorBorder px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleAllInCategory(category)}
                        className="flex items-center gap-2 py-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                      >
                        <Checkbox
                          checked={isCategoryFullyChecked(category)}
                          className={cn(
                            'data-[state=checked]:bg-primary data-[state=checked]:border-primary',
                            isCategoryPartiallyChecked(category) &&
                              'data-[state=unchecked]:bg-primary/30 data-[state=unchecked]:border-primary/50'
                          )}
                        />
                        {isCategoryFullyChecked(category)
                          ? t('deselect_all')
                          : t('select_all')}
                      </button>

                      <div className="space-y-0.5">
                        {visiblePermissions.map((permission) => (
                          <label
                            key={permission}
                            className="flex items-center gap-3 py-2 px-1 rounded-md hover:bg-elevated/30 transition-colors cursor-pointer"
                          >
                            <Checkbox
                              checked={isPermissionChecked(category, permission)}
                              onCheckedChange={() => togglePermission(category, permission)}
                              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <span className="text-sm text-colorText">
                              {t(`permissions.${permission}`, { defaultValue: permission })}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </MotionDiv>
                )}
              </AnimatePresence>
            </div>
          </MotionDiv>
        );
      })}
    </MotionDiv>
  );
}
